/**
 * Chat → /request hand-off (owner request 2026-08-21): when the chat has no
 * professionals to propose (recommend came back EMPTY, or the checklist branch
 * hit a gap), the honest next step is the matching-request form — and the user
 * has already answered most of it in the chat, so the form should not ask
 * twice.
 *
 * Mirrors `contact-prefill.ts` exactly: localStorage (since 2026-08-23 — the
 * hand-off opens in a NEW TAB, and a noopener tab starts with an empty
 * sessionStorage; see contact-prefill.ts), TTL, cleared on read —
 * NEVER URL params, because `description` is the user's own need in prose
 * (query strings are logged, shared and indexed; house rule: no personal data
 * in URLs).
 *
 * `buildRequestPrefill` is PURE and code-owned: every value comes from the
 * validated slot record, never from model text. Since Change 1 (2026-08-27)
 * that includes `description` — the model-written summary died with the
 * confirmation turn, and its replacement is COMPOSED here from the enum
 * slots, in English only (the prompt always forced the summary to English
 * anyway, so the form's language is unchanged). Model prose no longer
 * reaches the request form at all, which retires the "unvetted text reaches
 * a human matcher" risk outright. The category value lands in a
 * form whose submit re-validates them against the same vocabularies, so a
 * stale or tampered stored value degrades to an unselected field, not a bad
 * submit.
 */
import { cityFilter, INTENT_TO_CATEGORIES, type Intent, type Slots } from "./slots";
import { formatLocation } from "@/lib/provinces";

export const REQUEST_PREFILL_KEY = "et_chat_request_prefill";
export const REQUEST_PREFILL_TTL_MS = 10 * 60 * 1000;

export interface RequestPrefill {
  /** A DIRECTORY_CATEGORIES value (the intent's FIRST mapped category) or "". */
  category: string;
  /** A LOCATION_SLUGS value or "". */
  location: string;
  /** A code-composed EN line from the enum slots, or "" with no intent. */
  description: string;
}

/** Plain-English nouns for the composed description — deliberately NOT the
 *  chat namespace (this module is client-imported by RequestForm; pulling the
 *  translator in would drag the messages JSON into that bundle). */
const INTENT_NOUN: Record<Intent, string> = {
  company_formation: "company formation",
  work_permit_visa: "work permit / visa",
  property: "property",
  tax_accounting: "tax & accounting",
  insurance: "insurance",
  relocation: "relocation",
  translation_notary: "translation / notary",
  permits_licences: "permits & licences",
  hr_payroll: "HR & payroll",
  legal_help: "legal",
  other: "",
};
// The form no longer has a timeline field (2026-10-01), so timing the reader
// VOLUNTEERED in the chat reaches the team only through this sentence.
const TIMELINE_PHRASE: Record<string, string> = {
  ASAP: "needed as soon as possible",
  "Within a month": "needed within a month",
  "1-3 months": "needed in 1-3 months",
  // "Just exploring" is not a deadline; the sentence simply omits it.
};
const BUDGET_PHRASE: Record<string, string> = {
  lt_5m: "budget under 5M VND",
  "5_20m": "budget 5-20M VND",
  "20_50m": "budget 20-50M VND",
  gt_50m: "budget over 50M VND",
  // unsure: no phrase.
};

/** "Looking for a work permit / visa professional in Ho Chi Minh City,
 *  needed within a month, budget 20-50M VND." — built only from validated
 *  enum values, so it cannot carry a prohibited word or an invented fact. */
export function composeRequestDescription(slots: Slots): string {
  const noun = slots.intent ? INTENT_NOUN[slots.intent] : "";
  if (!noun) return "";
  const parts = [
    `Looking for a ${noun} professional${cityFilter(slots.city) ? ` in ${formatLocation(slots.city!, "en")}` : ""}`,
    ...(slots.timeline && TIMELINE_PHRASE[slots.timeline] ? [TIMELINE_PHRASE[slots.timeline]] : []),
    ...(slots.budget && BUDGET_PHRASE[slots.budget] ? [BUDGET_PHRASE[slots.budget]] : []),
  ];
  return `${parts.join(", ")}.`;
}

/** What the chat already knows, shaped for the /request form. Pure. */
export function buildRequestPrefill(slots: Slots): RequestPrefill {
  return {
    category: slots.intent ? (INTENT_TO_CATEGORIES[slots.intent][0] ?? "") : "",
    // cityFilter, like the description line above — `any` and `remote` are not
    // members of LOCATION_SLUGS, so passing one through made the /request
    // Select render its PLACEHOLDER (the field looks empty) while its hidden
    // input posted "any", sailed past `if (!location)` because the string is
    // truthy, and was rejected server-side by the enum. A user who said "any
    // city is fine", got no results and tapped the hand-off hit that.
    location: cityFilter(slots.city) ?? "",
    description: composeRequestDescription(slots),
  };
}

export function writeRequestPrefill(p: RequestPrefill): void {
  try {
    localStorage.setItem(REQUEST_PREFILL_KEY, JSON.stringify({ ...p, description: p.description.slice(0, 500), at: Date.now() }));
  } catch { /* private mode */ }
}

/** Returns the prefill if fresh, clearing the entry either way. */
export function takeRequestPrefill(now = Date.now()): RequestPrefill | null {
  try {
    const raw = localStorage.getItem(REQUEST_PREFILL_KEY);
    if (!raw) return null;
    localStorage.removeItem(REQUEST_PREFILL_KEY);
    const v = JSON.parse(raw) as Partial<RequestPrefill & { at: number }>;
    if (typeof v.at !== "number" || now - v.at > REQUEST_PREFILL_TTL_MS) return null;
    return {
      category: typeof v.category === "string" ? v.category : "",
      location: typeof v.location === "string" ? v.location : "",
      description: typeof v.description === "string" ? v.description.slice(0, 500) : "",
    };
  } catch {
    return null;
  }
}
